import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import { ApolloQueryResult } from '@apollo/client';
import Form, { Field, FormFooter, FormHeader, FormSection, ErrorMessage } from '@atlaskit/form';
import TextArea from '@atlaskit/textarea';
import Textfield from '@atlaskit/textfield';
import {
  ErrorComponents,
  InfoPanel,
  successNotificationBuilder,
  useFormSubmit,
  useNotification,
  useSidebar,
  useFeatureToggle,
  NotificationTypes,
} from '@css-xp/base';
import { resendInvoice, updateInvoiceWithMemo } from '@atlassian/temp-proxies';
import { BillingDocWithLineItems, InvoiceWithLineItems, UpdateInvoiceRequest } from '@atlassian/cssxp-types-billing';
import { ENABLE_SUPPORT_TICKET_AND_NOTE } from '@atlassian/cssxp-utils-generic';
import { SourceSystem } from '@atlassian/cssxp-typescommon';
import React, { memo } from 'react';
import { AnalyticsTrackAction, AnalyticsTrackSubject, analytics } from '@atlassian/temp-analytics';

interface FormData {
  memo?: string;
}

interface Props {
  withInputMemoFieldFlag: boolean;
  billingDoc?: BillingDocWithLineItems;
  refetch?: () => Promise<ApolloQueryResult<unknown>>;
}

const InvoiceUpdatePane = ({ withInputMemoFieldFlag = true, billingDoc, refetch }: Props): JSX.Element | null => {
  const { closeSidebar } = useSidebar();
  const [isEnabled] = useFeatureToggle();

  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<UpdateInvoiceRequest, any>({
    submitFormAction: withInputMemoFieldFlag ? updateInvoiceWithMemo : resendInvoice,
    onComplete: () => {
      closeSidebar();
      if (refetch) {
        refetch();
      }
    },
  });

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const MEMO_LENGTH_LIMIT = 500;
  const invoice = billingDoc as InvoiceWithLineItems;
  const transactionAccountId = invoice?.transactionAccountId ?? '';

  if (!invoice) {
    return null;
  }

  if (invoice?.sourceSystem === SourceSystem.HAMS) {
    return null;
  }

  const requestInvoiceUpdateData = (formData: FormData) => {
    const requestData: UpdateInvoiceRequest = {
      memo: (formData.memo ?? '').slice(0, MEMO_LENGTH_LIMIT),
      invoiceId: invoice.id,
      transactionAccountId,
    };

    return requestData;
  };

  const handleFormSubmit = (formData: FormData) => {
    const requestData = requestInvoiceUpdateData(formData);
    submitForm(requestData);
  };

  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('update-invoice-success')
      .withHeader('Successfully updated and resent invoice.')
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    analytics.sendTrackEvent({
      action: AnalyticsTrackAction.REQUEST,
      actionSubject: AnalyticsTrackSubject.RESENT_INVOICE,
      attributes: {
        invoiceId: invoice.id,
        transactionAccountId,
      },
    });
    setNotificationSent(true);

    closeSidebar();
  }

  return (
    <div className="invoice-update-container" style={{ maxWidth: '600px' }} data-testId="invoice-update-container">
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormHeader
              title={withInputMemoFieldFlag ? 'Resend invoice (with optional memo)' : 'Resend invoice'}
            ></FormHeader>
            <FormSection>
              {formError ? (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to update the invoice for the following reason"
                      message={formError}
                    />
                  }
                />
              ) : !transactionAccountId ? (
                <InfoPanel
                  type="warning"
                  title={withInputMemoFieldFlag ? 'Cannot add memo' : 'Cannot resend invoice'}
                  message={'There is no transaction account id on this invoice'}
                />
              ) : (
                <InfoPanel
                  type="information"
                  title="Invoice will be sent to:"
                  message={''}
                  detailList={[
                    () => (
                      <ul>
                        <li>all the billing admins in this billing account.</li>
                        <li>all the invoice recipients in this billing profile.</li>
                      </ul>
                    ),
                  ]}
                />
              )}
            </FormSection>
            {isEnabled(ENABLE_SUPPORT_TICKET_AND_NOTE) && (
              <FormSection>
                <Field
                  name="supportTicketNumber"
                  label="Support ticket number"
                  isRequired
                  validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
                >
                  {({ fieldProps, error }) => (
                    <>
                      <Textfield
                        {...fieldProps}
                        name="supportTicketNumber"
                        testId="support-ticket-number"
                        placeholder={'Add GSAC ticket number'}
                      />
                      {error && <ErrorMessage>Enter a valid ticket number</ErrorMessage>}
                    </>
                  )}
                </Field>
              </FormSection>
            )}
            {withInputMemoFieldFlag && (
              <FormSection>
                <Field<string, HTMLTextAreaElement> name="memo" label="(Optional) External memo">
                  {({ fieldProps }) => (
                    <TextArea
                      {...fieldProps}
                      placeholder="Add notes (Max limit: 500 characters, external memo is shown in PDF)"
                      name="memo"
                      defaultValue={invoice?.memo}
                      testId={'update-memo-field'}
                    />
                  )}
                </Field>
              </FormSection>
            )}

            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="default" onClick={() => closeSidebar()} testId="cancel-btn">
                  Cancel
                </Button>
                <LoadingButton appearance="primary" type="submit" isLoading={isProcessing} data-testId="update-invoice">
                  Resend
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};

export default memo(InvoiceUpdatePane);
